import { useSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useActiveFusionWriter } from '~/hooks/target/useActiveFusionWriter';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { useAuthSession } from '~/contexts/auth/AuthSessionContext';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { frameRegistrationRefusal } from '~/domain/dicom/uids';
import { DomainError } from '~/domain/errors/DomainError';
import {
    domainEventBus,
    RegistrationApprovalStatusChangeRequestedEvent,
    RegistrationCreateRequestedEvent,
    RegistrationDeleteRequestedEvent,
    RegistrationInvertRequestedEvent,
    RegistrationRenameRequestedEvent,
} from '~/domain/events';
import { computeCenterAlignmentTransform } from '~/domain/patient/registrationInitialAlignment';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { useReviewer } from '~/hooks/domain/useReviewer';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { localization } from '~/i18n';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { selectedRegistrationState } from '~/state/atoms/registration/registrationAtoms';
import { saveRequestedState } from '~/state/atoms/saveAtoms';
import { pendingAutoRegistrationState } from '~/state/atoms/toolsAtoms';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';

/**
 * Application-layer hook for registration domain events.
 *
 * Sits between the UI and domain layers following DDD layered
 * architecture (see CLAUDE.md). It subscribes to registration domain
 * events and forwards them to the session's {@link RegistrationService},
 * which wraps every mutation in `tracker.command()`/`batch()`. Going
 * through the service is what keeps mutations from throwing: the entity
 * mutators call `tracker.markChanged(...)`, which throws unless wrapped
 * in a command/batch.
 *
 * Mounted once at the app root via useDomainEventHandlers(), so it must
 * tolerate a not-yet-loaded session: we read it via useSessionOrNull()
 * and no-op when absent (this is also why we cannot use
 * useRegistrationCommands() here — that hook calls useSession(), which
 * throws when no patient is loaded). The service-backed snackbar +
 * unsaved-changes behaviour is replicated locally instead.
 *
 * No store context is pushed any more: the service and projections read
 * the live session patient directly.
 */
export function useRegistrationHandlers() {
    const session = useSessionOrNull();
    const selectedPrimaryImage = useSelectedPrimaryImage();
    const { setEnvironmentPrimaryImage } = useViewportEnvironment();
    const { fuse } = useActiveFusionWriter();
    const sessionData = useRecoilValue(sessionDataAtom);
    const setSelectedRegistration = useSetRecoilState(
        selectedRegistrationState
    );
    const selectedRegistration = useRecoilValue(selectedRegistrationState);
    const { enqueueSnackbar } = useSnackbar();
    const { userName } = useAuthSession();
    const reviewer = useReviewer();
    const setSaveRequested = useSetRecoilState(saveRequestedState);
    const setPendingAutoRegistration = useSetRecoilState(
        pendingAutoRegistrationState
    );

    const contentCreatorName = sessionData?.username ?? userName ?? 'unknown';

    // Mirrors useRegistrationCommands.wrap: an applied mutation emits a
    // changeset, which arms the unsaved-changes guard via
    // PendingChangesStore/PendingChangesRecoilBridge. A DomainError
    // surfaces as a warning snackbar and, like a genuine no-op
    // (create()/invert() returning undefined), emits no changeset so the
    // guard stays unarmed; any other error propagates (fail fast).
    const runCommand = useCallback(
        <T>(op: () => T): T | undefined => {
            try {
                return op();
            } catch (e) {
                if (e instanceof DomainError) {
                    enqueueSnackbar(e.message, { variant: 'warning' });
                    return undefined;
                }
                throw e;
            }
        },
        [enqueueSnackbar]
    );

    const handleCreateRequest = useCallback(
        (event: RegistrationCreateRequestedEvent) => {
            if (!session) {
                return;
            }
            const { patient, registrationService } = session;
            const primaryImage = patient.images.find(
                (i) => i.seriesUid === event.primaryImageSeriesUid
            );
            const secondaryImage = patient.images.find(
                (i) => i.seriesUid === event.secondaryImageSeriesUid
            );
            if (!primaryImage || !secondaryImage) {
                return;
            }
            // A registration maps one frame of reference onto another, so
            // both must have one, and two images on one frame need none.
            // Either refusal is said, never swallowed.
            const refusal = frameRegistrationRefusal(
                primaryImage.frameOfReferenceUid,
                secondaryImage.frameOfReferenceUid
            );
            if (refusal === 'frameUnknown') {
                enqueueSnackbar(
                    localization.t('registration.noFrameOfReference'),
                    { variant: 'warning' }
                );
                return;
            }
            if (refusal === 'sameFrame') {
                enqueueSnackbar(
                    localization.t('registration.sharedForNoRegistration'),
                    { variant: 'info' }
                );
                return;
            }
            // Seed the initial transform with a translation that aligns
            // the secondary volume's center to the primary's. Identity
            // initialization can place the secondary image entirely
            // outside the primary's field of view when the two DICOM
            // origins differ significantly.
            const initialTransform = computeCenterAlignmentTransform(
                primaryImage,
                secondaryImage
            );
            const savedReg = runCommand(() =>
                registrationService.create(
                    primaryImage,
                    secondaryImage,
                    initialTransform,
                    contentCreatorName
                )
            );
            // The pair is shown once the registration is aligned, so its
            // images never jump from the starting alignment to the fit.
            if (savedReg) {
                setPendingAutoRegistration({
                    registrationSopInstanceUid: savedReg.sopInstanceUid,
                    primarySeriesUid: primaryImage.seriesUid,
                    secondarySeriesUid: secondaryImage.seriesUid,
                });
            }
        },
        [
            session,
            runCommand,
            contentCreatorName,
            enqueueSnackbar,
            setPendingAutoRegistration,
        ]
    );

    const handleDeleteRequest = useCallback(
        (event: RegistrationDeleteRequestedEvent) => {
            if (!session) {
                return;
            }
            const reg = session.patient.getRegistrationWithSopInstanceUid(
                event.registrationSopInstanceUid
            );
            // Already gone — no-op safely.
            if (!reg) {
                return;
            }
            const wasSelected =
                selectedRegistration?.sopUid === reg.sopInstanceUid;
            const removed =
                runCommand(() => {
                    session.registrationService.remove({ id: reg.id });
                    return true;
                }) ?? false;
            // Clear selection only when the removal actually succeeded. A
            // blocked remove (e.g. an approved registration throws a
            // DomainError that runCommand swallows) must leave selection
            // intact, otherwise the UI would deselect a still-present
            // registration.
            if (removed && wasSelected) {
                setSelectedRegistration(null);
            }
            // No grid row may stay fused through a registration the patient
            // no longer holds: its tools would edit an object no save can
            // reach.
            if (removed) {
                getDatasetEnvironmentRegistry().clearFusionsThrough(reg);
            }
        },
        [session, runCommand, selectedRegistration, setSelectedRegistration]
    );

    const handleRenameRequest = useCallback(
        (event: RegistrationRenameRequestedEvent) => {
            if (!session) {
                return;
            }
            const reg = session.patient.getRegistrationWithSopInstanceUid(
                event.registrationSopInstanceUid
            );
            if (!reg) {
                return;
            }
            runCommand(() => {
                session.registrationService.rename(
                    { id: reg.id },
                    event.newLabel
                );
                return true;
            });
        },
        [session, runCommand]
    );

    const handleApprovalStatusChangeRequest = useCallback(
        (event: RegistrationApprovalStatusChangeRequestedEvent) => {
            if (!session) {
                return;
            }
            const reg = session.patient.getRegistrationWithSopInstanceUid(
                event.registrationSopInstanceUid
            );
            if (!reg) {
                return;
            }
            const ref = { id: reg.id };
            const status = event.newApprovalStatus as DICOMApprovalStatus;
            // Clinical sign-off must be attributable: the reviewer is the
            // logged-in user, same resolution as the structure-set flow.
            // (Application state only — the REG file has no approval
            // attribute; the connector row is the sole carrier.)
            const applied = runCommand(() => {
                switch (status) {
                    case DICOMApprovalStatus.APPROVED:
                        session.registrationService.approve(ref, reviewer);
                        break;
                    case DICOMApprovalStatus.REJECTED:
                        session.registrationService.reject(ref, reviewer);
                        break;
                    case DICOMApprovalStatus.UNAPPROVED:
                        session.registrationService.unapprove(ref);
                        break;
                }
                return true;
            });
            // Safety measure (parity with structure sets): a user-confirmed
            // approval transition requests an immediate save of all pending
            // changes, so a sign-off never exists only in browser memory.
            if (applied && event.source === 'user') {
                setSaveRequested((count) => count + 1);
            }
        },
        [session, runCommand, reviewer, setSaveRequested]
    );

    const handleInvertRequest = useCallback(
        (event: RegistrationInvertRequestedEvent) => {
            if (!session) {
                return;
            }
            const { patient, registrationService } = session;
            const reg = patient.getRegistrationWithSopInstanceUid(
                event.registrationSopInstanceUid
            );
            if (!reg) {
                return;
            }

            const result = runCommand(() =>
                registrationService.invert({ id: reg.id }, contentCreatorName)
            );
            if (!result) {
                return;
            }

            const { registration, newPrimaryImage, newSecondaryImage } = result;

            // The registration's source image becomes the selected primary;
            // on the grid that names its row as the pair's. A pair displayed
            // through an inverse registration already has it selected, and
            // re-selecting the same image would reset the Environment. A
            // refused switch fuses nothing: the pair is the new primary's.
            if (
                newPrimaryImage !== selectedPrimaryImage &&
                !setEnvironmentPrimaryImage(
                    [newPrimaryImage],
                    patient.patientId
                )
            ) {
                return;
            }

            // The other image is fused onto it through the inverted
            // registration. Once that pair shows, no row keeps the pair
            // through the original, so one alignment of the two images is
            // on screen and a drag cannot move one row and not the other.
            if (fuse(newPrimaryImage, newSecondaryImage, registration)) {
                getDatasetEnvironmentRegistry().clearFusionsThrough(reg);
            }
        },
        [
            session,
            runCommand,
            contentCreatorName,
            selectedPrimaryImage,
            setEnvironmentPrimaryImage,
            fuse,
        ]
    );

    useEffect(() => {
        const subscriptions = [
            domainEventBus.subscribe(
                RegistrationCreateRequestedEvent.EVENT_TYPE,
                handleCreateRequest
            ),
            domainEventBus.subscribe(
                RegistrationDeleteRequestedEvent.EVENT_TYPE,
                handleDeleteRequest
            ),
            domainEventBus.subscribe(
                RegistrationRenameRequestedEvent.EVENT_TYPE,
                handleRenameRequest
            ),
            domainEventBus.subscribe(
                RegistrationApprovalStatusChangeRequestedEvent.EVENT_TYPE,
                handleApprovalStatusChangeRequest
            ),
            domainEventBus.subscribe(
                RegistrationInvertRequestedEvent.EVENT_TYPE,
                handleInvertRequest
            ),
        ];

        return () => {
            subscriptions.forEach((sub) => sub.unsubscribe());
        };
    }, [
        handleCreateRequest,
        handleDeleteRequest,
        handleRenameRequest,
        handleApprovalStatusChangeRequest,
        handleInvertRequest,
    ]);
}
